/**
 * Fixed-point decimal backed by bigint, scaled by 10^18.
 *
 * All money, price and quantity math goes through this type. JS floats are
 * never used for balances or PnL. Every operation that can lose precision
 * (mul, div, round) takes an explicit rounding mode.
 */
export const SCALE_DP = 18;
const SCALE = 10n ** 18n;
/** Integer division of n by d (d > 0) with the given rounding. */
function divRound(n, d, mode) {
    if (d === 0n)
        throw new RangeError('Division by zero');
    if (d < 0n) {
        n = -n;
        d = -d;
    }
    const q = n / d; // truncates toward zero
    const r = n % d;
    if (r === 0n)
        return q;
    const neg = n < 0n;
    switch (mode) {
        case 'down':
            return q;
        case 'up':
            return neg ? q - 1n : q + 1n;
        case 'floor':
            return neg ? q - 1n : q;
        case 'ceil':
            return neg ? q : q + 1n;
        case 'half_up': {
            const twice = (r < 0n ? -r : r) * 2n;
            if (twice >= d)
                return neg ? q - 1n : q + 1n;
            return q;
        }
    }
}
function parse(s) {
    const str = s.trim();
    const m = /^([+-])?(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/.exec(str);
    if (!m || (m[2] === '' && (m[3] === undefined || m[3] === ''))) {
        throw new TypeError(`Invalid decimal: "${s}"`);
    }
    const sign = m[1] === '-' ? -1n : 1n;
    let intPart = m[2] || '0';
    let frac = m[3] ?? '';
    const exp = m[4] ? parseInt(m[4], 10) : 0;
    if (exp > 0) {
        const shift = frac.slice(0, exp).padEnd(exp, '0');
        intPart = intPart + shift;
        frac = frac.slice(exp);
    }
    else if (exp < 0) {
        const n = -exp;
        const padded = intPart.padStart(n + 1, '0');
        frac = padded.slice(padded.length - n) + frac;
        intPart = padded.slice(0, padded.length - n);
    }
    // Truncate beyond 18 dp toward zero; inputs with more precision are rare
    // and callers that care round explicitly before.
    const fracScaled = (frac + '0'.repeat(SCALE_DP)).slice(0, SCALE_DP);
    return sign * (BigInt(intPart) * SCALE + BigInt(fracScaled));
}
export class D {
    /** Raw scaled value (value × 10^18). */
    raw;
    constructor(raw) {
        this.raw = raw;
    }
    static ZERO = new D(0n);
    static ONE = new D(SCALE);
    static fromRaw(raw) {
        return new D(raw);
    }
    /**
     * Build from a decimal string, bigint (integer units) or number.
     * Numbers are accepted for constants only and go through their shortest
     * string form, so 0.1 becomes exactly 0.1.
     */
    static from(x) {
        if (x instanceof D)
            return x;
        if (typeof x === 'bigint')
            return new D(x * SCALE);
        if (typeof x === 'number') {
            if (!Number.isFinite(x))
                throw new TypeError(`Invalid decimal: ${x}`);
            return new D(parse(String(x)));
        }
        return new D(parse(x));
    }
    /** Like from() but returns null for null/undefined/empty string. */
    static opt(x) {
        if (x === null || x === undefined || x === '')
            return null;
        return D.from(x);
    }
    static isValid(s) {
        try {
            parse(s);
            return true;
        }
        catch {
            return false;
        }
    }
    add(o) {
        return new D(this.raw + D.from(o).raw);
    }
    sub(o) {
        return new D(this.raw - D.from(o).raw);
    }
    mul(o, mode = 'half_up') {
        return new D(divRound(this.raw * D.from(o).raw, SCALE, mode));
    }
    div(o, mode = 'half_up') {
        const d = D.from(o).raw;
        return new D(divRound(this.raw * SCALE, d, mode));
    }
    neg() {
        return new D(-this.raw);
    }
    abs() {
        return this.raw < 0n ? new D(-this.raw) : this;
    }
    /** Round to `dp` decimal places. */
    round(dp, mode = 'half_up') {
        if (dp >= SCALE_DP)
            return this;
        const unit = 10n ** BigInt(SCALE_DP - dp);
        return new D(divRound(this.raw, unit, mode) * unit);
    }
    cmp(o) {
        const b = D.from(o).raw;
        return this.raw < b ? -1 : this.raw > b ? 1 : 0;
    }
    eq(o) {
        return this.cmp(o) === 0;
    }
    lt(o) {
        return this.cmp(o) < 0;
    }
    lte(o) {
        return this.cmp(o) <= 0;
    }
    gt(o) {
        return this.cmp(o) > 0;
    }
    gte(o) {
        return this.cmp(o) >= 0;
    }
    isZero() {
        return this.raw === 0n;
    }
    isNeg() {
        return this.raw < 0n;
    }
    isPos() {
        return this.raw > 0n;
    }
    static min(a, b) {
        const x = D.from(a);
        const y = D.from(b);
        return x.lte(y) ? x : y;
    }
    static max(a, b) {
        const x = D.from(a);
        const y = D.from(b);
        return x.gte(y) ? x : y;
    }
    /** Canonical string: no trailing zeros, no exponent. */
    toString() {
        const neg = this.raw < 0n;
        const abs = neg ? -this.raw : this.raw;
        const int = abs / SCALE;
        let frac = (abs % SCALE).toString().padStart(SCALE_DP, '0').replace(/0+$/, '');
        const s = frac ? `${int}.${frac}` : `${int}`;
        return neg && abs !== 0n ? `-${s}` : s;
    }
    /** Fixed number of decimals, rounding as requested. */
    toFixed(dp, mode = 'half_up') {
        const r = this.round(dp, mode);
        const neg = r.raw < 0n;
        const abs = neg ? -r.raw : r.raw;
        const int = abs / SCALE;
        if (dp === 0)
            return `${neg && int !== 0n ? '-' : ''}${int}`;
        const frac = (abs % SCALE).toString().padStart(SCALE_DP, '0').slice(0, dp);
        return `${neg && abs !== 0n ? '-' : ''}${int}.${frac}`;
    }
    /** For display/charting only. Never feed the result back into money math. */
    toNumber() {
        return Number(this.toString());
    }
    toJSON() {
        return this.toString();
    }
}
/** Shorthand for D.from */
export const d = (x) => D.from(x);
//# sourceMappingURL=decimal.js.map